import { buildQueryString, request } from "../client"
import type { ContactOrder, ContactOrderStep } from "../contact-orders"
import type { ScheduleEventCommunicationPresentation } from "../work-orders"

export type CaseCommunicationDirection = "INBOUND" | "OUTBOUND" | string
export type CaseCommunicationChannel =
  | "EMAIL"
  | "SMS"
  | "RCS"
  | "WHATSAPP"
  | "LETTER"
  | "PHONE"
  | "VOICE"
  | "CHAT"
  | "PORTAL"
  | "BOT_API"
  | "ADMIN"
  | string
export type CaseCommunicationPurpose =
  | "APPOINTMENT_ANNOUNCEMENT"
  | "APPOINTMENT_CONFIRMATION"
  | "APPOINTMENT_RESCHEDULE"
  | "APPOINTMENT_CANCELLATION"
  | "APPOINTMENT_REMINDER"
  | "WRONG_RECIPIENT"
  | "CONTACT_DATA_UPDATE"
  | "CALLBACK_REQUEST"
  | "GENERAL_INQUIRY"
  | "HANDOVER"
  | "LETTER_DELIVERY"
  | "FOLLOW_UP"
  | string
export type CaseCommunicationLifecycleStatus =
  | "PLANNED"
  | "QUEUED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "FAILED"
  | "CANCELED"
  | "SUPPRESSED"
  | "NEEDS_REVIEW"
  | string
export type CaseCommunicationDeliveryStatus =
  | "NONE"
  | "PREPARED"
  | "QUEUED"
  | "SENT"
  | "ACCEPTED"
  | "DELIVERED"
  | "READ"
  | "MANUAL_PENDING"
  | "MANUAL_DELIVERED"
  | "FAILED"
  | string
export type CaseCommunicationBusinessOutcome =
  | "NONE"
  | "INFORMED"
  | "CONFIRMED"
  | "RESCHEDULED"
  | "CANCELED"
  | "WRONG_RECIPIENT"
  | "CONTACT_UPDATED"
  | "CALLBACK_REQUESTED"
  | "HANDOVER_CREATED"
  | "NO_RESPONSE"
  | "EXHAUSTED"
  | string

export type CaseCommunicationLinkRole =
  | "PRIMARY_SUBJECT"
  | "WORK_ORDER"
  | "SCHEDULE_EVENT"
  | "PROPERTY"
  | "CONTACT_POINT"
  | "CONTACT_ORDER"
  | "CONTACT_STEP"
  | "OUTBOUND_EXECUTION"
  | "LETTER_BATCH"
  | "LETTER_BATCH_ITEM"
  | "ACTION_CASE"
  | "SESSION"
  | "CONVERSATION"
  | "PROVIDER_OBJECT"
  | "CAUSE"
  | "RESULT"

export type CaseCommunicationLink = {
  id: string
  communication_id: string
  ref_service: string
  ref_resource: string
  ref_id: string
  role: CaseCommunicationLinkRole
  created_at: string
}

export type CaseCommunicationEvent = {
  id: string
  communication_id: string
  event_type: string
  source: string
  occurred_at: string
  provider?: string | null
  provider_ref?: string | null
  created_at: string
}

export type CaseCommunicationLetterDelivery = {
  delivery_mode: "automatic" | "manual" | null
  print_mode: "PERSONAL_LETTER" | "IMPERSONAL_LETTER" | "GENERIC_KLEBEZETTEL" | null
  target: {
    display_name: string | null
    address: string | null
  } | null
  property: {
    display_name: string | null
    address: string | null
    unit_label: string | null
  } | null
  source: "communication_snapshot" | "batch_selection" | "undocumented"
}

export type CaseCommunication = {
  id: string
  organization_id: string
  direction: CaseCommunicationDirection
  channel: CaseCommunicationChannel
  purpose: CaseCommunicationPurpose
  lifecycle_status: CaseCommunicationLifecycleStatus
  delivery_status: CaseCommunicationDeliveryStatus
  business_outcome: CaseCommunicationBusinessOutcome
  origin_kind: string
  case_id?: string | null
  contact_order_id?: string | null
  contact_step_id?: string | null
  template_key?: string | null
  template_label?: string | null
  template_version?: string | null
  provider?: string | null
  provider_ref?: string | null
  provider_status?: string | null
  planned_at?: string | null
  due_at?: string | null
  queued_at?: string | null
  sent_at?: string | null
  accepted_at?: string | null
  delivered_at?: string | null
  read_at?: string | null
  received_at?: string | null
  completed_at?: string | null
  failed_at?: string | null
  canceled_at?: string | null
  suppressed_at?: string | null
  created_at: string
  updated_at: string
}

export type CaseCommunicationTimelineItem = {
  communication: CaseCommunication
  events: CaseCommunicationEvent[]
  links: CaseCommunicationLink[]
  appointment_refs?: Array<{
    role: string
    schedule_event_id: string
    start_at?: string | null
    end_at?: string | null
    status?: string | null
    flex_type?: string | null
    timeframe_start_at?: string | null
    timeframe_end_at?: string | null
    customer_window_start_at?: string | null
    customer_window_end_at?: string | null
    employee_id?: string | null
    employee_name?: string | null
    appointment_contact_name?: string | null
    appointment_contact_phone?: string | null
    appointment_contact_email?: string | null
    confirmed_by_label?: string | null
    communication_presentation?: ScheduleEventCommunicationPresentation | null
  }>
  letter_delivery?: CaseCommunicationLetterDelivery | null
  kehrblatt_extension?: KehrblattCaseCommunicationExtensionData | null
}

export type CaseCommunicationFeedProjection = "table" | "history"
export type CaseCommunicationFeedSortDirection = "asc" | "desc"
export type CaseCommunicationFeedSortField = "effective_at" | "work_order_number"
export type CaseCommunicationFeedStatusGroup = "PLANNED" | "DUE" | "ACTIVE" | "UNSUCCESSFUL"

export type CaseCommunicationFeedQuery = {
  organizationId: string
  caseId?: string | null
  contactOrderId?: string | null
  contactStepId?: string | null
  refService?: string | null
  refResource?: string | null
  refId?: string | null
  refRole?: CaseCommunicationLinkRole | null
  projection?: CaseCommunicationFeedProjection
  sortDirection?: CaseCommunicationFeedSortDirection
  sortBy?: CaseCommunicationFeedSortField
  statusGroups?: CaseCommunicationFeedStatusGroup[] | null
  searchQuery?: string | null
  appointmentDateFrom?: string | null
  appointmentDateTo?: string | null
  employeeIds?: string[] | null
  channels?: string[] | null
  limit?: number
  cursor?: string | null
}

export type CaseCommunicationFeedCapabilities = {
  can_open_work_order: boolean
  can_open_automation: boolean
  can_test_step: boolean
  can_manage_contact_order: boolean
  can_edit_step: boolean
  can_execute_now: boolean
  can_retry: boolean
  can_skip_wait: boolean
  can_cancel_step: boolean
  can_manage_letter: boolean
  available_channels: string[]
}

export type KehrblattCaseCommunicationRecipient = {
  customer_id?: string | null
  contact_point_id?: string | null
  display_name?: string | null
  role?: string | null
  kind?: string | null
  label?: string | null
  target_value?: string | null
  is_verified?: boolean | null
  lifecycle_status?: string | null
  source?: string | null
  is_usable?: boolean | null
}

export type KehrblattCaseCommunicationWorkOrder = {
  id: string
  case_id?: string | null
  order_number?: string | null
  status?: string | null
  view_status?: string | null
  property_id?: string | null
  property_unit_id?: string | null
  property_unit_label?: string | null
  order_types?: WorkOrderOrderTypeItem[] | null
  due_date?: string | null
  estimated_duration_min?: number | null
  current_schedule_event_id?: string | null
}

export type KehrblattCaseCommunicationProperty = {
  id: string
  public_code?: string | null
  street?: string | null
  house_number?: string | null
  zip?: string | null
  city?: string | null
  country?: string | null
  address_extra?: string | null
  unit_id?: string | null
  unit_label?: string | null
}

export type KehrblattCaseCommunicationAppointment = {
  role: "old" | "new" | "current" | "canceled"
  schedule_event_id: string
  start_at?: string | null
  end_at?: string | null
  status?: string | null
  flex_type?: string | null
  timeframe_start_at?: string | null
  timeframe_end_at?: string | null
  customer_window_start_at?: string | null
  customer_window_end_at?: string | null
  employee_id?: string | null
  employee_name?: string | null
  appointment_contact_name?: string | null
  appointment_contact_phone?: string | null
  appointment_contact_email?: string | null
  confirmed_by_label?: string | null
  communication_presentation?: ScheduleEventCommunicationPresentation | null
}

export type KehrblattCaseCommunicationCustomerContext = {
  customers?: Array<{
    id: string
    display_name: string
  }>
  contact_points?: Array<{
    id: string
    customer_id?: string | null
    property_id?: string | null
    kind: string
    label?: string | null
    target_value: string
    is_verified: boolean
    source: string
    lifecycle_status: string
  }>
}

export type KehrblattCaseCommunicationContent = {
  subject?: string | null
}

export type KehrblattCaseCommunicationStatusDetails = {
  failure_reason?: string | null
  skipped_reason?: string | null
  suppression_reason?: string | null
}

export type KehrblattCaseCommunicationProviderProgress = {
  status?: string | null
  provider_status?: string | null
  detail?: string | null
  dispatch_date?: string | null
  tracking_code?: string | null
}

export type KehrblattCaseCommunicationAutomationContext = {
  session_id?: string | null
  project_ref?: string | null
  realization_ref?: string | null
  organization_slug?: string | null
}

export type ContactStepOutcomeKind =
  | "RESULT"
  | "SKIPPED"
  | "NOT_REQUIRED"
  | "PLAN_CLOSED"
  | "UNKNOWN_CANCELED"

export type ContactStepOutcomeReason =
  | "COMPLETED"
  | "APPOINTMENT_CONFIRMED"
  | "APPOINTMENT_SCHEDULED"
  | "APPOINTMENT_DECLINED"
  | "NO_RESPONSE"
  | "NOT_REACHED"
  | "INVALID_CONTACT"
  | "WRONG_RECIPIENT"
  | "DELIVERY_FAILED"
  | "TECHNICAL_FAILURE"
  | "MANUAL_CANCEL"
  | "WAIT_SKIPPED"
  | "AUTOMATIC_SKIP"
  | "APPOINTMENT_CANCELED"
  | "APPOINTMENT_COMPLETED"
  | "APPOINTMENT_RESCHEDULED"
  | "PLAN_CANCELED"
  | "PRODUCT_RESOLVED"
  | "UNKNOWN"

export type ContactStepOutcomeActorType = "ADMIN" | "BOT" | "PORTAL" | "SYSTEM" | "UNKNOWN"

/** Optional Kehrblatt runtime read model for the terminal step projection. */
export type ContactStepOutcomeReadModel = {
  kind: ContactStepOutcomeKind
  reason: ContactStepOutcomeReason
  occurred_at?: string | null
  subject_label?: string | null
  recorded_by?: {
    actor_type: ContactStepOutcomeActorType
    label?: string | null
  } | null
}

export type KehrblattCaseCommunicationExtensionData = {
  letter_dispatch?: {
    planned_mode: "MANUAL" | "AUTOMATIC" | null
    manual_action_required: boolean
    needs_review: boolean
  } | null
  contact_plan?: Pick<
    ContactOrder,
    | "work_order_id"
    | "schedule_event_id"
    | "reason"
    | "proposed_start_at"
    | "proposed_end_at"
    | "assigned_employee_ids"
    | "contact_sequence_template_id"
  > | null
  contact_step?: Pick<
    ContactOrderStep,
    | "recipient_customer_id"
    | "recipient_role"
    | "template_key"
    | "letter_mode"
    | "postal_contact_point_id"
    | "delivery_due_at"
    | "subject_template_override"
    | "body_template_override"
  > | null
  recipient?: KehrblattCaseCommunicationRecipient | null
  work_order?: KehrblattCaseCommunicationWorkOrder | null
  property?: KehrblattCaseCommunicationProperty | null
  appointments: KehrblattCaseCommunicationAppointment[]
  template?: {
    key?: string | null
    label?: string | null
    version?: string | null
  } | null
  content?: KehrblattCaseCommunicationContent | null
  status_details?: KehrblattCaseCommunicationStatusDetails | null
  provider_progress?: KehrblattCaseCommunicationProviderProgress | null
  automation_context?: KehrblattCaseCommunicationAutomationContext | null
  step_outcome?: ContactStepOutcomeReadModel | null
  letter_delivery?: CaseCommunicationLetterDelivery | null
  capabilities: CaseCommunicationFeedCapabilities
  customer_context?: KehrblattCaseCommunicationCustomerContext | null
}

export type CaseCommunicationFeedExtension = {
  namespace: string
  version: number
  data: Record<string, unknown>
}

export type CaseCommunicationContactOrderSummary = {
  id: string
  organization_id: string
  case_id: string
  status: string
  closed_reason?: string | null
  closed_reason_details?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export type CaseCommunicationFeedStep = {
  id: string
  contact_order_id: string
  position: number
  channel: string
  execution_mode: ContactOrderStep["execution_mode"]
  automatic_delivery_committed: boolean
  destination?: ContactOrderStep["destination"]
  public_code?: string | null
  due_at?: string | null
  status: string
  title?: string | null
  notes?: string | null
  retries_on_no_answer: number
  result_status?: string | null
  result_description?: string | null
  result_meta: Record<string, unknown>
  result_recorded_at?: string | null
  response_deadline_at?: string | null
  next_action_at?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export type CaseCommunicationFeedLink = {
  ref_service: string
  ref_resource: string
  ref_id: string
  role: CaseCommunicationLinkRole
}

export type CaseCommunicationFeedEvent = {
  id: string
  event_type: string
  source: string
  occurred_at: string
  provider?: string | null
  provider_ref?: string | null
}

export type CaseCommunicationFeedCommunication = {
  id: string
  organization_id: string
  case_id?: string | null
  contact_order_id?: string | null
  contact_step_id?: string | null
  direction: string
  channel: string
  purpose: string
  lifecycle_status: string
  delivery_status: string
  business_outcome: string
  origin_kind: string
  provider?: string | null
  provider_ref?: string | null
  provider_status?: string | null
  planned_at?: string | null
  due_at?: string | null
  queued_at?: string | null
  sent_at?: string | null
  accepted_at?: string | null
  delivered_at?: string | null
  read_at?: string | null
  received_at?: string | null
  completed_at?: string | null
  failed_at?: string | null
  canceled_at?: string | null
  suppressed_at?: string | null
  created_at?: string | null
  updated_at?: string | null
  events: CaseCommunicationFeedEvent[]
  links: CaseCommunicationFeedLink[]
}

export type CaseCommunicationFeedEntry = {
  entry_key: string
  kind: "contact_step" | "case_communication"
  representation: "planned" | "recorded"
  effective_at: string
  organization_id: string
  case_id?: string | null
  contact_order_id?: string | null
  contact_step_id?: string | null
  communication_id?: string | null
  contact_order_summary?: CaseCommunicationContactOrderSummary | null
  step?: CaseCommunicationFeedStep | null
  communication?: CaseCommunicationFeedCommunication | null
  extensions?: CaseCommunicationFeedExtension[]
}

export type CaseCommunicationFeedResult = {
  items: CaseCommunicationFeedEntry[]
  contact_order_summaries: CaseCommunicationContactOrderSummary[]
  total: number
  next_cursor?: string | null
}

export type AdaptedCaseCommunicationScheduleEvent = ScheduleEvent & {
  communication_presentation?: ScheduleEventCommunicationPresentation | null
}

export type AdaptedCaseCommunicationFeed = {
  entries: CaseCommunicationFeedEntry[]
  tableEntries: AdaptedCommunicationPlanFeedEntry[]
  contactOrders: ContactOrder[]
  caseCommunications: CaseCommunicationTimelineItem[]
  workOrders: WorkOrder[]
  properties: Property[]
  scheduleEvents: AdaptedCaseCommunicationScheduleEvent[]
  customers: Customer[]
  contactPoints: ContactPoint[]
  allowedChannels: string[]
  capabilitiesByEntryKey: Record<string, CaseCommunicationFeedCapabilities>
  extensionByEntryKey: Record<string, KehrblattCaseCommunicationExtensionData>
  entryKeyByStepId: Record<string, string>
  entryKeyByCommunicationId: Record<string, string>
}

type AdaptedCommunicationPlanFeedEntryBase = {
  entryKey: string
  representation: CaseCommunicationFeedEntry["representation"]
  effectiveAt: string
  extension: KehrblattCaseCommunicationExtensionData | null
  capabilities: CaseCommunicationFeedCapabilities | null
}

export type AdaptedContactStepFeedEntry = AdaptedCommunicationPlanFeedEntryBase & {
  kind: "contact_step"
  order: ContactOrder
  step: ContactOrderStep
  communication: CaseCommunicationTimelineItem | null
}

export type AdaptedStandaloneCommunicationFeedEntry = AdaptedCommunicationPlanFeedEntryBase & {
  kind: "case_communication"
  order: ContactOrder | null
  communication: CaseCommunicationTimelineItem
}

export type AdaptedCommunicationPlanFeedEntry =
  | AdaptedContactStepFeedEntry
  | AdaptedStandaloneCommunicationFeedEntry

const kehrblattExtension = (
  entry: CaseCommunicationFeedEntry,
): KehrblattCaseCommunicationExtensionData | null => {
  const extension = (entry.extensions ?? []).find(
    (candidate) => candidate.namespace === "kehrblatt" && candidate.version === 2,
  )

  return extension ? (extension.data as KehrblattCaseCommunicationExtensionData) : null
}

export const getKehrblattCaseCommunicationExtension = kehrblattExtension

const putById = <T extends { id?: string | null }>(
  target: Map<string, T>,
  values: T[] | null | undefined,
) => {
  for (const value of values ?? []) {
    if (value?.id) target.set(value.id, value)
  }
}

const adaptContactOrderSummary = (
  summary: CaseCommunicationContactOrderSummary,
  productPlan: NonNullable<KehrblattCaseCommunicationExtensionData["contact_plan"]>,
  current?: ContactOrder,
): ContactOrder => ({
  id: summary.id,
  organization_id: summary.organization_id,
  case_id: summary.case_id ?? null,
  ...productPlan,
  status: summary.status as ContactOrder["status"],
  closed_reason: (summary.closed_reason ?? null) as ContactOrder["closed_reason"],
  closed_reason_details: summary.closed_reason_details ?? null,
  steps: current?.steps ?? [],
  created_at: summary.created_at ?? null,
  updated_at: summary.updated_at ?? null,
})

const adaptFeedStep = (
  step: CaseCommunicationFeedStep,
  extension: KehrblattCaseCommunicationExtensionData | null,
): ContactOrderStep => {
  const selection = extension?.contact_step

  if (!selection) throw new Error("Die Produktauswahl des Kontaktschritts fehlt.")

  return {
    id: step.id,
    organization_id: undefined,
    contact_order_id: step.contact_order_id,
    position: step.position,
    channel: step.channel,
    execution_mode: step.execution_mode,
    automatic_delivery_committed: step.automatic_delivery_committed,
    destination: step.destination ?? null,
    ...selection,
    due_at: step.due_at ?? null,
    status: step.status as ContactOrderStep["status"],
    title: step.title ?? null,
    notes: step.notes ?? null,
    message_kind: extension?.template?.key ?? null,
    message_label: extension?.template?.label ?? null,
    template_label: extension?.template?.label ?? null,
    retries_on_no_answer: step.retries_on_no_answer,
    response_deadline_at: step.response_deadline_at ?? null,
    next_action_at: step.next_action_at ?? null,
    result_status: (step.result_status ?? null) as ContactOrderStep["result_status"],
    result_description: step.result_description ?? null,
    result_meta: step.result_meta ?? {},
    result_recorded_at: step.result_recorded_at ?? null,
  }
}

const adaptFeedCommunication = (
  entry: CaseCommunicationFeedEntry,
  extension: KehrblattCaseCommunicationExtensionData | null,
): CaseCommunicationTimelineItem | null => {
  const source = entry.communication

  if (!source) return null

  const { events, links } = source
  const communication: CaseCommunication = {
    id: source.id,
    organization_id: source.organization_id,
    direction: source.direction,
    channel: source.channel,
    purpose: source.purpose,
    lifecycle_status: source.lifecycle_status,
    delivery_status: source.delivery_status,
    business_outcome: source.business_outcome,
    origin_kind: source.origin_kind,
    case_id: source.case_id ?? null,
    contact_order_id: source.contact_order_id ?? null,
    contact_step_id: source.contact_step_id ?? null,
    template_key: extension?.template?.key ?? null,
    template_label: extension?.template?.label ?? null,
    template_version: extension?.template?.version ?? null,
    provider: source.provider ?? null,
    provider_ref: source.provider_ref ?? null,
    provider_status: source.provider_status ?? null,
    planned_at: source.planned_at ?? null,
    due_at: source.due_at ?? null,
    queued_at: source.queued_at ?? null,
    sent_at: source.sent_at ?? null,
    accepted_at: source.accepted_at ?? null,
    delivered_at: source.delivered_at ?? null,
    read_at: source.read_at ?? null,
    received_at: source.received_at ?? null,
    completed_at: source.completed_at ?? null,
    failed_at: source.failed_at ?? null,
    canceled_at: source.canceled_at ?? null,
    suppressed_at: source.suppressed_at ?? null,
    created_at: source.created_at ?? entry.effective_at,
    updated_at: source.updated_at ?? source.created_at ?? entry.effective_at,
  }

  return {
    communication,
    events: events.map((event) => ({
      id: event.id,
      communication_id: source.id,
      event_type: event.event_type,
      source: event.source,
      occurred_at: event.occurred_at,
      provider: event.provider ?? null,
      provider_ref: event.provider_ref ?? null,
      created_at: event.occurred_at,
    })),
    links: links.map((link) => ({
      id: `${source.id}:${link.role}:${link.ref_service}:${link.ref_resource}:${link.ref_id}`,
      communication_id: source.id,
      ref_service: link.ref_service,
      ref_resource: link.ref_resource,
      ref_id: link.ref_id,
      role: link.role,
      created_at: source.created_at ?? entry.effective_at,
    })),
    appointment_refs: extension?.appointments ?? [],
    letter_delivery: extension?.letter_delivery ?? null,
    kehrblatt_extension: extension,
  }
}

export const adaptCaseCommunicationFeed = (
  result: CaseCommunicationFeedResult,
): AdaptedCaseCommunicationFeed => {
  const orders = new Map<string, ContactOrder>()
  const workOrders = new Map<string, WorkOrder>()
  const properties = new Map<string, Property>()
  const scheduleEvents = new Map<string, AdaptedCaseCommunicationScheduleEvent>()
  const customers = new Map<string, Customer>()
  const contactPoints = new Map<string, ContactPoint>()
  const communications = new Map<string, CaseCommunicationTimelineItem>()
  const allowedChannels = new Set<string>()
  const capabilitiesByEntryKey: Record<string, CaseCommunicationFeedCapabilities> = {}
  const extensionByEntryKey: Record<string, KehrblattCaseCommunicationExtensionData> = {}
  const entryKeyByStepId: Record<string, string> = {}
  const entryKeyByCommunicationId: Record<string, string> = {}

  const putOrder = (
    summary: CaseCommunicationContactOrderSummary | null | undefined,
    productPlan: KehrblattCaseCommunicationExtensionData["contact_plan"],
  ) => {
    if (!summary?.id) return

    if (!productPlan) throw new Error("Die Terminmitteilung zum Ankündigungsplan fehlt.")

    const current = orders.get(summary.id)
    orders.set(summary.id, adaptContactOrderSummary(summary, productPlan, current))
  }

  const summaries = new Map(
    (result.contact_order_summaries ?? []).map((summary) => [summary.id, summary]),
  )

  for (const entry of result.items ?? []) {
    const extension = kehrblattExtension(entry)

    if (entry.contact_order_id) {
      putOrder(
        entry.contact_order_summary ?? summaries.get(entry.contact_order_id),
        extension?.contact_plan,
      )
    }

    if (entry.contact_order_id && entry.step) {
      const current = orders.get(entry.contact_order_id)

      if (!current) throw new Error("Der Ankündigungsplan des Schritts fehlt.")

      const currentSteps = current?.steps ?? []
      const stepIndex = currentSteps.findIndex((candidate) => candidate.id === entry.step?.id)
      const adaptedStep = adaptFeedStep(entry.step, extension)
      const steps =
        stepIndex >= 0
          ? currentSteps.map((candidate, index) => (index === stepIndex ? adaptedStep : candidate))
          : [...currentSteps, adaptedStep]
      orders.set(entry.contact_order_id, {
        ...current,
        steps,
      })
    }

    if (extension) {
      extensionByEntryKey[entry.entry_key] = extension

      if (extension.work_order?.id) {
        const workOrderExtension = extension.work_order
        workOrders.set(workOrderExtension.id, {
          id: workOrderExtension.id,
          organization_id: entry.organization_id,
          can_manage: false,
          case_id: workOrderExtension.case_id ?? entry.case_id ?? null,
          property_id: workOrderExtension.property_id ?? "",
          property_unit_id: workOrderExtension.property_unit_id ?? "",
          property_unit_label: workOrderExtension.property_unit_label ?? null,
          recipients: [],
          order_number: workOrderExtension.order_number ?? null,
          order_types: workOrderExtension.order_types ?? [],
          due_date: workOrderExtension.due_date ?? null,
          status: (workOrderExtension.status || "OPEN") as WorkOrder["status"],
          view_status: (workOrderExtension.view_status ?? null) as WorkOrder["view_status"],
          priority: null,
          estimated_duration_min: workOrderExtension.estimated_duration_min ?? null,
          allowed_employee_ids: null,
          assigned_employee_id: null,
          current_schedule_event_id: workOrderExtension.current_schedule_event_id ?? null,
          has_schedule_events: Boolean(extension.appointments?.length),
          has_active_schedule_events: Boolean(extension.appointments?.length),
          next_action_at: null,
          archived_at: null,
          archive_reason: null,
          created_at: "",
          updated_at: "",
        })

        if (entry.contact_order_id) {
          const current = orders.get(entry.contact_order_id)

          if (current) {
            current.work_order_id = extension.work_order.id
            const currentAppointment =
              (extension.appointments ?? []).find(
                (appointment) => appointment.role === "current",
              ) ?? extension.appointments?.[0]

            if (currentAppointment?.schedule_event_id) {
              current.schedule_event_id = currentAppointment.schedule_event_id
            }
          }
        }
      }

      if (extension.property?.id) {
        const property = extension.property
        properties.set(property.id, {
          id: property.id,
          organization_id: entry.organization_id,
          public_code: property.public_code ?? null,
          street: property.street ?? "",
          house_number: property.house_number ?? null,
          zip: property.zip ?? null,
          city: property.city ?? null,
          country: property.country ?? null,
          address_extra: property.address_extra ?? null,
          geo_lat: null,
          geo_lng: null,
          primary_contact_customer_id: null,
          archived_at: null,
          archive_reason: null,
          units:
            property.unit_id || property.unit_label
              ? [
                  {
                    id: property.unit_id ?? `${property.id}:unit`,
                    organization_id: entry.organization_id,
                    property_id: property.id,
                    unit_label: property.unit_label ?? "",
                    unit_type: "RESIDENTIAL",
                    archived_at: null,
                    archive_reason: null,
                    effective_archived_at: null,
                    effective_archive_reason: null,
                    archive_source: null,
                    notes: null,
                    created_at: "",
                  },
                ]
              : [],
          created_at: "",
          updated_at: "",
        })
      }

      for (const appointment of extension.appointments ?? []) {
        if (!appointment.schedule_event_id) continue

        scheduleEvents.set(appointment.schedule_event_id, {
          id: appointment.schedule_event_id,
          organization_id: entry.organization_id,
          can_manage: false,
          work_order_id: extension.work_order?.id ?? null,
          property_id: extension.property?.id ?? null,
          employee_id: appointment.employee_id ?? null,
          employee_name: appointment.employee_name ?? null,
          event_type: "APPOINTMENT",
          flex_type: (appointment.flex_type || "HARD") as ScheduleEvent["flex_type"],
          status: (appointment.status || "RESERVED") as ScheduleEvent["status"],
          planning_flag: "OK",
          start_at: appointment.start_at ?? "",
          end_at: appointment.end_at ?? appointment.start_at ?? "",
          timeframe_start_at: appointment.timeframe_start_at ?? null,
          timeframe_end_at: appointment.timeframe_end_at ?? null,
          customer_window_start_at: appointment.customer_window_start_at ?? null,
          customer_window_end_at: appointment.customer_window_end_at ?? null,
          all_day: false,
          title: null,
          description: null,
          customer_note: null,
          location_text: null,
          source: "SYSTEM",
          calendar_sync: null,
          created_at: "",
          updated_at: "",
          communication_presentation: appointment.communication_presentation ?? null,
        })
      }
      const recipient = extension.recipient

      if (recipient?.customer_id) {
        customers.set(recipient.customer_id, {
          id: recipient.customer_id,
          organization_id: entry.organization_id,
          display_name: recipient.display_name ?? recipient.label ?? recipient.target_value ?? "",
          type: "PERSON",
          customer_facing_name_mode: "USE_DISPLAY_NAME",
          role: "OTHER",
          channel_priorities: { EMAIL: 0, SMS: 0, PHONE: 0, LETTER: 0 },
          archived_at: null,
          archive_reason: null,
          notes: null,
          created_at: "",
          updated_at: "",
        })
      }

      if (recipient?.contact_point_id) {
        contactPoints.set(recipient.contact_point_id, {
          id: recipient.contact_point_id,
          organization_id: entry.organization_id,
          customer_id: recipient.customer_id ?? null,
          property_id: extension.property?.id ?? null,
          kind: recipient.kind ?? "",
          value: recipient.target_value ?? "",
          normalized_value: recipient.target_value ?? "",
          label: recipient.label ?? null,
          is_primary: false,
          is_verified: recipient.is_verified ?? false,
          source: recipient.source ?? "feed",
          lifecycle_status: recipient.lifecycle_status ?? null,
          created_at: "",
        } as ContactPoint)
      }

      putById(
        customers,
        extension.customer_context?.customers?.map((customer) => ({
          id: customer.id,
          organization_id: entry.organization_id,
          display_name: customer.display_name,
          type: "PERSON" as const,
          customer_facing_name_mode: "USE_DISPLAY_NAME" as const,
          role: "OTHER" as const,
          channel_priorities: { EMAIL: 0, SMS: 0, PHONE: 0, LETTER: 0 },
          archived_at: null,
          archive_reason: null,
          notes: null,
          created_at: "",
          updated_at: "",
        })),
      )
      putById(
        contactPoints,
        extension.customer_context?.contact_points?.map((contactPoint) => ({
          id: contactPoint.id,
          organization_id: entry.organization_id,
          customer_id: contactPoint.customer_id ?? null,
          property_id: contactPoint.property_id ?? null,
          kind: contactPoint.kind as ContactPoint["kind"],
          label: contactPoint.label ?? null,
          value: contactPoint.target_value,
          normalized_value: contactPoint.target_value,
          is_primary: false,
          is_verified: contactPoint.is_verified,
          source: contactPoint.source as ContactPoint["source"],
          lifecycle_status: contactPoint.lifecycle_status,
          created_at: "",
        })),
      )
      for (const channel of extension.capabilities.available_channels) {
        if (channel) allowedChannels.add(channel)
      }
      capabilitiesByEntryKey[entry.entry_key] = extension.capabilities
    }

    if (entry.step?.id) entryKeyByStepId[entry.step.id] = entry.entry_key

    if (entry.communication?.id) {
      const communication = adaptFeedCommunication(entry, extension)

      if (communication) {
        communications.set(communication.communication.id, communication)
        entryKeyByCommunicationId[communication.communication.id] = entry.entry_key
      }
    }
  }

  const tableEntries = (result.items ?? []).flatMap<AdaptedCommunicationPlanFeedEntry>((entry) => {
    const extension = extensionByEntryKey[entry.entry_key] ?? null
    const capabilities = extension?.capabilities ?? null
    const order = entry.contact_order_id ? (orders.get(entry.contact_order_id) ?? null) : null
    const communication = entry.communication?.id
      ? (communications.get(entry.communication.id) ?? null)
      : null

    if (entry.kind === "contact_step" && order && entry.step?.id) {
      const step = adaptFeedStep(entry.step, extension)

      return [
        {
          entryKey: entry.entry_key,
          kind: "contact_step",
          representation: entry.representation,
          effectiveAt: entry.effective_at,
          extension,
          capabilities,
          order: {
            ...order,
            steps: (order.steps ?? []).map((candidate) =>
              candidate.id === step.id ? step : candidate,
            ),
          },
          step,
          communication,
        },
      ]
    }

    if (entry.kind === "case_communication" && communication) {
      return [
        {
          entryKey: entry.entry_key,
          kind: "case_communication",
          representation: entry.representation,
          effectiveAt: entry.effective_at,
          extension,
          capabilities,
          order,
          communication,
        },
      ]
    }

    return []
  })

  return {
    entries: result.items ?? [],
    tableEntries,
    contactOrders: Array.from(orders.values()),
    caseCommunications: Array.from(communications.values()),
    workOrders: Array.from(workOrders.values()),
    properties: Array.from(properties.values()),
    scheduleEvents: Array.from(scheduleEvents.values()),
    customers: Array.from(customers.values()),
    contactPoints: Array.from(contactPoints.values()),
    allowedChannels: Array.from(allowedChannels),
    capabilitiesByEntryKey,
    extensionByEntryKey,
    entryKeyByStepId,
    entryKeyByCommunicationId,
  }
}

export const fetchCaseCommunicationFeed = (
  query: CaseCommunicationFeedQuery,
  options: { signal?: AbortSignal } = {},
) => {
  const path = `/case-communications${buildQueryString({
    organization_id: query.organizationId,
    case_id: query.caseId,
    contact_order_id: query.contactOrderId,
    contact_step_id: query.contactStepId,
    ref_service: query.refService,
    ref_resource: query.refResource,
    ref_id: query.refId,
    ref_role: query.refRole,
    projection: query.projection ?? "table",
    sort_direction: query.sortDirection,
    sort_by: query.sortBy,
    status_groups: query.statusGroups,
    context_search_query: query.searchQuery,
    context_date_from: query.appointmentDateFrom,
    context_date_to: query.appointmentDateTo,
    context_actor_ids: query.employeeIds,
    channels: query.channels,
    limit: query.limit,
    cursor: query.cursor,
  })}`

  return options.signal
    ? request<CaseCommunicationFeedResult>(path, { signal: options.signal })
    : request<CaseCommunicationFeedResult>(path)
}
